import { castToBoolean } from "../util";
import { changedEvaluationSettings, pickEvaluationSettings } from './evaluationSettings';

const loadedEvaluationSettings = new WeakMap();

class Field {
  constructor() {
    let data = arguments[0] || {};
    this.id = data.id || null;
    this.name = data.name || '';
    this.name_l10n = data.name_l10n || [];
    this.reporting = data.reporting || '';
    this.options = data.options || [];
    this.filteredOptions = [];
    this.type = data.type || null;
    this.type_name = data.type_name || null;
    this.notes = data.notes || null;
    this.notes_l10n = data.notes_l10n || [];
    this.use_info_disclosure_text = castToBoolean(data.use_info_disclosure_text) || null;
    this.info_disclosure_text = data.info_disclosure_text || null;
    this.info_disclosure_text_l10n = data.info_disclosure_text_l10n || [];
    // Optional custom color for the notes text (gated by the toggle).
    this.use_notes_color = castToBoolean(data.use_notes_color) || null;
    this.notes_color = data.notes_color || null;
    this.body = data.body || null;
    this.raw = data.raw || null;
    this.fieldType = data.fieldType || null;
    this.enabledValidationTypes = data.enabledValidationTypes || [];
    this.nonTargetEnabledValidationTypes = data.nonTargetEnabledValidationTypes || [];
    this.hasEnabledValidationTypes = data.hasEnabledValidationTypes ;
    this.events_custom_task_properties_id = data.events_custom_task_properties_id || null;
    this.data_link_field_names = data.data_link_field_names || [];
    this.data_link_info = data.data_link_info || {};

    // Image formats
    this.image = data.image || null;
    this.thumb = data.thumb || null;
    this.preview = data.preview || null;

    // Formula
    this.formula = data.formula || null;

    Object.assign(this, pickEvaluationSettings(data));
    loadedEvaluationSettings.set(this, JSON.parse(JSON.stringify(pickEvaluationSettings(data))));
    if (Array.isArray(this.evaluation_rep_note_label_l10n)) {
      this.evaluation_rep_note_label_l10n = {};
    }

    // Handle case where options are passed as an object instead of an array
    // This works around an issue where the options are built as a map instead of an array in the API
    if (this.options.constructor !== Array) {
      this.options = Object.values(this.options);
    }
  }

  get isRedacted() {
    if (this.data_link_info) {
      const keys = Object.keys(this.data_link_info);
      for (const key of keys) {
        const link = this.data_link_info[key];
        if (link && castToBoolean(link.redact)) {
          return true;
        }
      }
    }
    return false;
  }

  get evaluationSettings() {
    const changed = changedEvaluationSettings(pickEvaluationSettings(this), loadedEvaluationSettings.get(this) || {});
    if (!Object.keys(changed).length) {
      return null;
    }
    return { id: this.id, ...changed };
  }

  hasOptions() {
    return this.options.length > 0;
  };

  getType() {
    return this.fieldType.name || this.type_name || this.type;
  };

  getField() {
    return this;
  };

  getLabelText() {
    let s = '';
    if (this.id) {
      s += this.id + ': ';
    }
    if (this.name) {
      s += this.name;
    } else {
      s += '(no name)';
    }
    return s;
  };

  get isSubsectionField() {
    return this.fieldType && this.fieldType.defined_as == 'FIELD_SUBSECTION';
  }

  get isTotalsField() {
    return this.fieldType && this.fieldType.defined_as == 'FIELD_TOTAL';
  }

  get isFormulaField() {
    return this.fieldType && this.fieldType.defined_as == 'FIELD_FORMULA';
  }

  get isDisplayTextField() {
    return this.fieldType && this.fieldType.defined_as == 'FIELD_DISPLAY_TEXT';
  }

  get isDisplayImageField() {
    return this.fieldType && this.fieldType.defined_as == 'FIELD_DISPLAY_IMAGE';
  }

  get isSelectWithNumericEquivalent() {
    return this.fieldType && this.fieldType.defined_as == 'FIELD_SELECT_WITH_NUMERIC_EQUIVALENT';
  }

  set options(options) {
    this._options = options;
    this._activeOptions = null;
  }

  get options() {
    return this._options;
  }

  get activeOptions() {
    if (!this._activeOptions) {
      const options = this.options || [];
      const active = options.filter(option => castToBoolean(option.active));
      const displayOrder = o => o.hasOwnProperty('display_order') ? parseInt(o.display_order) : 0;
      const compare = (a, b) => displayOrder(a) - displayOrder(b);
      const sorted = active.sort(compare);
      this._activeOptions = sorted;
    }
    return this._activeOptions;
  }
}

export default Field;
